'use client';

import React, { useState } from 'react';
import { BeforeAfterComponent } from '../types/content-components.types.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';
import { Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';

interface BeforeAfterProps {
  component: BeforeAfterComponent;
}

export const BeforeAfter: React.FC<BeforeAfterProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);
  const [sliderPosition, setSliderPosition] = useState(50);

  const animationClass = isVisible
    ? props.animation === 'fade-in'
      ? 'animate-fadeIn'
      : props.animation === 'slide-up'
      ? 'animate-slideUp'
      : ''
    : '';

  // Check if either image is missing (placeholder state)
  const needsBeforeImage = !props.before.imageUrl;
  const needsAfterImage = !props.after.imageUrl;

  if (needsBeforeImage || needsAfterImage) {
    return (
      <div ref={ref} className={`w-full ${animationClass}`} style={{ opacity: isVisible ? undefined : 0 }}>
        {props.title && (
          <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
        )}
        <div className="bg-learn-sky-50 border-2 border-dashed border-learn-sky-300 rounded-xl p-6 space-y-4">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-learn-sky-200 rounded-xl flex items-center justify-center mx-auto mb-3">
              <ImageIcon className="w-8 h-8 text-learn-sky-600" />
            </div>
            <h4 className="text-lg font-semibold text-learn-sky-900 mb-2">
              🖼️ Before/After Images Needed
            </h4>
            <p className="text-sm text-learn-sky-800">
              Generate these images and paste URLs back into the chat:
            </p>
          </div>

          {/* Before Image Prompt */}
          {needsBeforeImage && (
            <div className="bg-white rounded-lg p-4 border border-learn-sky-200">
              <p className="text-xs font-semibold text-learn-sky-700 uppercase tracking-wider mb-2">
                BEFORE Image
              </p>
              <p className="text-sm text-gray-700 font-mono leading-relaxed mb-2">
                {props.before.imagePrompt || props.before.description}
              </p>
              <p className="text-xs text-gray-600 italic">Label: "{props.before.label}"</p>
            </div>
          )}

          {/* After Image Prompt */}
          {needsAfterImage && (
            <div className="bg-white rounded-lg p-4 border border-learn-sky-200">
              <p className="text-xs font-semibold text-learn-sky-700 uppercase tracking-wider mb-2">
                AFTER Image
              </p>
              <p className="text-sm text-gray-700 font-mono leading-relaxed mb-2">
                {props.after.imagePrompt || props.after.description}
              </p>
              <p className="text-xs text-gray-600 italic">Label: "{props.after.label}"</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full implementation with images
  return (
    <div ref={ref} className={`w-full ${animationClass}`} style={{ opacity: isVisible ? undefined : 0 }}>
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
      )}

      {props.swipeable ? (
        // Swipeable/slider comparison
        <div className="relative rounded-xl overflow-hidden shadow-lg">
          <div className="relative w-full aspect-video">
            {/* Before Image (background) */}
            <img
              src={props.before.imageUrl!}
              alt={props.before.label}
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* After Image (clipped by slider) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src={props.after.imageUrl!}
                alt={props.after.label}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* Slider */}
            <div className="absolute inset-y-0 flex items-center" style={{ left: `${sliderPosition}%` }}>
              <div className="w-1 bg-white h-full shadow-lg"></div>
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center cursor-grab active:cursor-grabbing">
                <ChevronLeft className="w-4 h-4 text-gray-700 -ml-1" />
                <ChevronRight className="w-4 h-4 text-gray-700 -mr-1" />
              </div>
            </div>

            {/* Labels */}
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-70 text-white text-sm px-3 py-1 rounded">
              {props.before.label}
            </div>
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white text-sm px-3 py-1 rounded">
              {props.after.label}
            </div>
          </div>

          {/* Slider Control */}
          <input
            type="range"
            min="0"
            max="100"
            value={sliderPosition}
            onChange={(e) => setSliderPosition(Number(e.target.value))}
            className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-64 opacity-0 cursor-pointer"
          />
        </div>
      ) : (
        // Side-by-side comparison (stacks on mobile)
        <div className="grid md:grid-cols-2 gap-4">
          {/* Before */}
          <div className="space-y-2">
            <div className="bg-learn-sage-100 rounded-lg px-3 py-2 text-center">
              <p className="text-sm font-semibold text-learn-sage-900">{props.before.label}</p>
            </div>
            <img
              src={props.before.imageUrl!}
              alt={props.before.label}
              className="w-full rounded-lg shadow-md"
            />
            {props.before.description && (
              <p className="text-sm text-gray-700">{props.before.description}</p>
            )}
          </div>

          {/* After */}
          <div className="space-y-2">
            <div className="bg-green-100 rounded-lg px-3 py-2 text-center">
              <p className="text-sm font-semibold text-green-900">{props.after.label}</p>
            </div>
            <img
              src={props.after.imageUrl!}
              alt={props.after.label}
              className="w-full rounded-lg shadow-md"
            />
            {props.after.description && (
              <p className="text-sm text-gray-700">{props.after.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

